import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Camera,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  LogIn,
  LogOut,
  User,
  Calendar,
  Timer,
  CalendarDays,
  FileText,
  ChevronLeft,
  ChevronRight,
  Send,
  Eye,
  CalendarClock,
  Palmtree,
  HeartPulse,
  Ban,
  Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput, TimeInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AttendanceMarkingPage = () => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [status, setStatus] = useState('idle'); // idle, ready, capturing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [config, setConfig] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [stream, setStream] = useState(null);
  const [distance, setDistance] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [userWorkType, setUserWorkType] = useState('office'); // office or field
  
  // Calendar and Leave states
  const [activeTab, setActiveTab] = useState('mark');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveFormData, setLeaveFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  // Attendance Correction Request states
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [correctionFormData, setCorrectionFormData] = useState({
    date: '',
    correction_type: 'check_in',
    requested_time: '',
    reason: '',
    screenshot_url: ''
  });

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(deltaPhi / 2) ** 2 +
              Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Fetch attendance config and user's assigned location
  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // First get global config as fallback (with auth header)
      const configResponse = await axios.get(`${API_BASE_URL}/hr/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let effectiveConfig = configResponse.data;
      
      // Now get user's HR data to check for assigned location and shift
      if (user?.id) {
        try {
          const userResponse = await axios.get(
            `${API_BASE_URL}/hr/employees/${user.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const hrData = userResponse.data?.hr_data || {};
          const assignedLocationId = hrData.assigned_location_id;
          const workType = hrData.work_type || 'office';
          const assignedShiftId = hrData.assigned_shift_id;
          
          // Store work type for later use
          setUserWorkType(workType);
          
          // If user has an assigned shift, fetch its details
          if (assignedShiftId) {
            try {
              const shiftsResponse = await axios.get(
                `${API_BASE_URL}/hr/shifts`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              const shifts = shiftsResponse.data.shifts || [];
              const assignedShift = shifts.find(s => s.id === assignedShiftId);
              
              if (assignedShift) {
                effectiveConfig = {
                  ...effectiveConfig,
                  shift_start_time: assignedShift.start_time,
                  shift_end_time: assignedShift.end_time,
                  grace_period_minutes: assignedShift.grace_period_minutes ?? effectiveConfig.grace_period_minutes,
                  late_threshold_minutes: assignedShift.late_threshold_minutes ?? effectiveConfig.late_threshold_minutes,
                  shift_name: assignedShift.name
                };
              }
            } catch (shiftErr) {
              console.error('Error fetching shifts:', shiftErr);
            }
          }
          
          // If user has an assigned location, fetch its details
          if (assignedLocationId) {
            const locationsResponse = await axios.get(
              `${API_BASE_URL}/hr/locations`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const locations = locationsResponse.data.locations || [];
            const assignedLocation = locations.find(l => l.id === assignedLocationId);
            
            if (assignedLocation) {
              effectiveConfig = {
                ...effectiveConfig,
                office_latitude: assignedLocation.latitude,
                office_longitude: assignedLocation.longitude,
                allowed_radius_meters: assignedLocation.radius_meters || assignedLocation.allowed_radius_meters || effectiveConfig.allowed_radius_meters,
                location_name: assignedLocation.name
              };
            }
          } else if (workType === 'office') {
            // No assigned location - try to get first active location
            const locationsResponse = await axios.get(
              `${API_BASE_URL}/hr/locations`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const locations = locationsResponse.data.locations || [];
            if (locations.length > 0) {
              const firstLocation = locations[0];
              effectiveConfig = {
                ...effectiveConfig,
                office_latitude: firstLocation.latitude,
                office_longitude: firstLocation.longitude,
                allowed_radius_meters: firstLocation.radius_meters || firstLocation.allowed_radius_meters || effectiveConfig.allowed_radius_meters,
                location_name: firstLocation.name
              };
            }
          }
        } catch (err) {
          console.error('Error fetching user HR data:', err);
        }
      }
      
      setConfig(effectiveConfig);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, [user?.id]);

  // Check today's attendance
  const checkTodayAttendance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API_BASE_URL}/hr/attendance/records?user_id=${user.id}&start_date=${today}&end_date=${today}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.records.length > 0) {
        setTodayRecord(response.data.records[0]);
      }
    } catch (error) {
      console.error('Error checking today attendance:', error);
    }
  }, [user?.id]);

  // Get current location
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      return;
    }

    // Set a loading indicator
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setLocation(loc);
        setLocationError(null);
        // Lightweight feedback so the employee knows the manual
        // Refresh button actually picked up a fresh GPS fix. Includes
        // the accuracy radius (the smaller the better — operators
        // use this to decide whether to refresh again if it's >50m).
        if (position.coords.accuracy != null) {
          toast.success(
            `Location refreshed (±${Math.round(position.coords.accuracy)}m accuracy)`,
            { duration: 2000 }
          );
        }

        // Calculate distance from office if config loaded
        if (config) {
          const dist = calculateDistance(
            loc.latitude, loc.longitude,
            config.office_latitude, config.office_longitude
          );
          setDistance(Math.round(dist));
        }
      },
      (error) => {
        let errorMessage = 'Unable to get location';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location in your browser/device settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable. Please check your GPS settings.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Tap to retry.';
            break;
          default:
            errorMessage = error.message || 'Unknown location error';
        }
        setLocationError(errorMessage);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [config]);

  // Initialize and start location tracking immediately
  useEffect(() => {
    fetchConfig();
    checkTodayAttendance();
  }, [fetchConfig, checkTodayAttendance]);
  
  // Get location on mount
  useEffect(() => {
    getLocation();
  }, []);

  // Refresh location when config is loaded and periodically
  useEffect(() => {
    if (config) {
      getLocation();
      // Refresh location every 30 seconds
      const interval = setInterval(getLocation, 30000);
      return () => clearInterval(interval);
    }
  }, [config, getLocation]);

  // Fetch employee's attendance records for calendar
  const fetchAttendanceRecords = useCallback(async (month) => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const year = month.getFullYear();
      const monthNum = month.getMonth();
      const startDate = `${year}-${String(monthNum + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, monthNum + 1, 0).getDate();
      const endDate = `${year}-${String(monthNum + 1).padStart(2, '0')}-${lastDay}`;
      
      // Use the my-records endpoint for current user's own attendance
      const response = await axios.get(
        `${API_BASE_URL}/hr/attendance/my-records`,
        {
          params: { start_date: startDate, end_date: endDate },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const records = response.data?.records || [];
      console.log(`Attendance records: user_id=${response.data?.user_id}, found=${records.length} records for ${startDate} to ${endDate}`);
      setAttendanceRecords(records);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setAttendanceRecords([]);
    }
  }, [user?.id]);

  // Fetch shifts
  const fetchShifts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(response.data?.shifts || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  }, []);

  // Fetch employee data
  const fetchEmployeeData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/employees/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeData(response.data);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    }
  }, [user?.id]);

  // Fetch leave requests
  const fetchLeaveRequests = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/leave/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveRequests(response.data?.requests || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  }, [user?.id]);

  // Fetch leave balance
  const fetchLeaveBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/leave/my-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  }, [user?.id]);

  // Fetch calendar data when tab changes
  useEffect(() => {
    if (activeTab === 'calendar' || activeTab === 'leave') {
      fetchShifts();
      fetchEmployeeData();
      fetchAttendanceRecords(calendarMonth);
      fetchLeaveRequests();
      fetchLeaveBalance();
    }
  }, [activeTab, calendarMonth, fetchShifts, fetchEmployeeData, fetchAttendanceRecords, fetchLeaveRequests, fetchLeaveBalance]);

  // Fetch leave data on mount (for leave section always visible)
  useEffect(() => {
    fetchLeaveRequests();
    fetchLeaveBalance();
    fetchShifts();
    fetchEmployeeData();
    fetchAttendanceRecords(calendarMonth);
  }, [fetchLeaveRequests, fetchLeaveBalance, fetchShifts, fetchEmployeeData, fetchAttendanceRecords, calendarMonth]);

  // Submit leave request
  const handleSubmitLeave = async () => {
    if (!leaveFormData.start_date || !leaveFormData.end_date || !leaveFormData.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmittingLeave(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/leave/request`,
        {
          ...leaveFormData,
          user_id: user.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Leave request submitted successfully');
      setLeaveDialogOpen(false);
      setLeaveFormData({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchLeaveRequests();
    } catch (error) {
      console.error('Error submitting leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmittingLeave(false);
    }
  };

  // Fetch correction requests
  const fetchCorrectionRequests = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/my-correction-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrectionRequests(response.data?.requests || []);
    } catch (error) {
      console.error('Error fetching correction requests:', error);
    }
  }, [user?.id]);

  // Submit correction request
  const handleSubmitCorrection = async () => {
    if (!correctionFormData.date || !correctionFormData.requested_time || !correctionFormData.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmittingCorrection(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/attendance/correction-request`,
        correctionFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Correction request submitted successfully');
      setCorrectionDialogOpen(false);
      setCorrectionFormData({ date: '', correction_type: 'check_in', requested_time: '', reason: '', screenshot_url: '' });
      fetchCorrectionRequests();
    } catch (error) {
      console.error('Error submitting correction:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit correction request');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Also fetch correction requests on mount
  useEffect(() => {
    fetchCorrectionRequests();
  }, [fetchCorrectionRequests]);

  // Start camera
  const startCamera = async () => {
    setStatus('ready');
    setCapturedImage(null);
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', 
          width: { ideal: 640 }, 
          height: { ideal: 480 } 
        }
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setStatus('error');
      setErrorMessage('Failed to access camera. Please allow camera permissions.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setStatus('idle');
  };

  // Capture selfie
  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    setStatus('captured');  // Changed from 'capturing' to 'captured'
  };

  // Submit attendance
  const submitAttendance = async (action) => {
    if (!location) {
      toast.error('Location not available. Please enable GPS.');
      return;
    }

    if (action === 'check_in' && !capturedImage) {
      toast.error('Please capture a selfie first.');
      return;
    }

    setStatus('capturing');
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'check_in' 
        ? `${API_BASE_URL}/hr/attendance/check-in`
        : `${API_BASE_URL}/hr/attendance/check-out`;
      
      // Build request data based on action
      const requestData = {
        latitude: location.latitude,
        longitude: location.longitude
      };
      
      // Check-in requires additional fields
      if (action === 'check_in') {
        requestData.method = 'selfie';
        requestData.face_image = capturedImage;
        requestData.device_id = navigator.userAgent;
      }
      
      const response = await axios.post(
        `${endpoint}?user_id=${user.id}`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus('success');
      toast.success(action === 'check_in' ? 'Check-in successful!' : 'Check-out successful!');
      
      // Refresh today's record
      await checkTodayAttendance();
      
      // Stop camera
      stopCamera();
      setCapturedImage(null);
      
    } catch (error) {
      console.error('Error submitting attendance:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to mark attendance');
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  // Format time in company timezone
  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const companyTimezone = config?.timezone_name || 'Asia/Dubai';
    try {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: companyTimezone
      });
    } catch {
      // Fallback if timezone is invalid
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  // Format time for calendar (24h format)
  const formatTimeForCalendar = (timeString) => {
    if (!timeString) return '--:--';
    const companyTimezone = config?.timezone_name || 'Asia/Dubai';
    try {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: companyTimezone
      });
    } catch {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  };

  // Calculate working hours
  const getWorkingHours = () => {
    if (!todayRecord?.check_in_time) return '--:--';
    
    const checkIn = new Date(todayRecord.check_in_time);
    const checkOut = todayRecord.check_out_time 
      ? new Date(todayRecord.check_out_time) 
      : new Date();
    
    const diffMs = checkOut - checkIn;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  // For field workers, always allow (no radius check needed)
  const isWithinRadius = userWorkType === 'field' || (distance !== null && config && distance <= config.allowed_radius_meters);
  const canCheckIn = !todayRecord?.check_in_time;
  const canCheckOut = todayRecord?.check_in_time && !todayRecord?.check_out_time;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Mark Attendance</h1>
          <p className="text-wt-text-muted">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Status */}
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader>
            <CardTitle className="text-wt-text-primary flex items-center gap-2">
              <Calendar className="h-5 w-5 text-wt-gold" />
              Today's Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-wt-bg rounded-lg">
                <LogIn className="h-6 w-6 mx-auto mb-2 text-green-400" />
                <p className="text-xs text-wt-text-muted">Check In</p>
                <p className="font-semibold text-wt-text-primary">
                  {formatTime(todayRecord?.check_in_time)}
                </p>
              </div>
              <div className="text-center p-4 bg-wt-bg rounded-lg">
                <LogOut className="h-6 w-6 mx-auto mb-2 text-red-400" />
                <p className="text-xs text-wt-text-muted">Check Out</p>
                <p className="font-semibold text-wt-text-primary">
                  {formatTime(todayRecord?.check_out_time)}
                </p>
              </div>
              <div className="text-center p-4 bg-wt-bg rounded-lg">
                <Timer className="h-6 w-6 mx-auto mb-2 text-blue-400" />
                <p className="text-xs text-wt-text-muted">Working</p>
                <p className="font-semibold text-wt-text-primary">{getWorkingHours()}</p>
              </div>
            </div>

            {todayRecord && (
              <div className={`p-3 rounded-lg ${
                todayRecord.status === 'present' ? 'bg-green-500/10 text-green-400' :
                todayRecord.status === 'late' ? 'bg-yellow-500/10 text-yellow-400' :
                todayRecord.status === 'half_day' ? 'bg-orange-500/10 text-orange-400' :
                'bg-red-500/10 text-red-400'
              }`}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium capitalize">{todayRecord.status?.replace('_', ' ')}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location Status */}
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader>
            <CardTitle className="text-wt-text-primary flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-wt-gold" />
                Location Status
              </span>
              {/* Always-visible Refresh button — operators sometimes
                  see a stale fix (employee moved closer, GPS drift,
                  or first reading was from a cached coarse location)
                  and need to force a fresh sample without reloading
                  the page. The button stays enabled in every state
                  (error / fetching / fixed) so it can also act as a
                  retry when no `locationError` was raised. */}
              <Button
                onClick={getLocation}
                variant="outline"
                size="sm"
                className="border-wt-gold/40 text-wt-gold hover:bg-wt-gold/10"
                data-testid="refresh-location-btn"
                title="Re-fetch GPS coordinates"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Location
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {locationError ? (
              <div className="p-4 bg-red-500/10 rounded-lg">
                <div className="flex items-center gap-2 text-red-400">
                  <XCircle className="h-5 w-5" />
                  <span>{locationError}</span>
                </div>
              </div>
            ) : location ? (
              <div className="space-y-3">
                {userWorkType === 'field' ? (
                  // Field workers - no radius validation needed
                  <div className="p-4 rounded-lg bg-blue-500/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-blue-400" />
                        <span className="text-blue-400">
                          Field Worker - Location captured
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Office workers - show radius status
                  <div className={`p-4 rounded-lg ${isWithinRadius ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isWithinRadius ? (
                          <CheckCircle className="h-5 w-5 text-green-400" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-400" />
                        )}
                        <span className={isWithinRadius ? 'text-green-400' : 'text-yellow-400'}>
                          {isWithinRadius ? `Within ${config?.location_name || 'office'} radius` : `Outside ${config?.location_name || 'office'} radius`}
                        </span>
                      </div>
                      <span className="text-wt-text-muted text-sm">
                        {distance}m away
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-wt-text-muted">
                  {config?.location_name && (
                    <p className="font-medium text-wt-text-secondary mb-1">{config.location_name}</p>
                  )}
                  <p>Location: {config?.office_latitude?.toFixed(4)}, {config?.office_longitude?.toFixed(4)}</p>
                  {userWorkType === 'office' && (
                    <p>Allowed radius: {config?.allowed_radius_meters}m</p>
                  )}
                  {userWorkType === 'field' && (
                    <p className="text-blue-400">No radius restriction for field workers</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-wt-text-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Getting location…</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Camera Section */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            <Camera className="h-5 w-5 text-wt-gold" />
            Selfie Verification
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'idle' && (
            <div className="text-center py-8">
              <User className="h-16 w-16 mx-auto mb-4 text-wt-text-muted" />
              <p className="text-wt-text-muted mb-4">
                Take a selfie to verify your attendance
              </p>
              <Button
                onClick={startCamera}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                disabled={!location || (!canCheckIn && !canCheckOut)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Open Camera
              </Button>
              {!canCheckIn && !canCheckOut && todayRecord && (
                <p className="text-sm text-wt-text-muted mt-2">
                  You've already checked in and out today.
                </p>
              )}
            </div>
          )}

          {(status === 'ready' || status === 'capturing') && !capturedImage && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-w-lg mx-auto">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Face guide */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-32 h-40 border-2 border-dashed border-wt-gold/50 rounded-full" />
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <Button onClick={stopCamera} variant="outline" className="border-wt-border">
                  Cancel
                </Button>
                <Button onClick={captureSelfie} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                  <Camera className="h-4 w-4 mr-2" />
                  Capture
                </Button>
              </div>
            </div>
          )}

          {capturedImage && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-w-lg mx-auto">
                <img src={capturedImage} alt="Captured selfie" className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <Button 
                  onClick={() => { setCapturedImage(null); setStatus('ready'); }} 
                  variant="outline" 
                  className="border-wt-border"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                
                {canCheckIn && (
                  <Button
                    onClick={() => submitAttendance('check_in')}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    disabled={status === 'capturing'}
                  >
                    {status === 'capturing' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <LogIn className="h-4 w-4 mr-2" />
                    )}
                    Check In
                  </Button>
                )}
                
                {canCheckOut && (
                  <Button
                    onClick={() => submitAttendance('check_out')}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={status === 'capturing'}
                  >
                    {status === 'capturing' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <LogOut className="h-4 w-4 mr-2" />
                    )}
                    Check Out
                  </Button>
                )}
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-400" />
              <p className="text-green-400 font-medium">Attendance marked successfully!</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <XCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
              <p className="text-red-400">{errorMessage}</p>
              <Button
                onClick={() => setStatus('idle')}
                variant="outline"
                className="mt-4 border-wt-border"
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shift Info */}
      {config && (
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-wt-gold" />
                <span className="text-wt-text-muted">Shift:</span>
                <span className="text-wt-text-primary">
                  {config.shift_start_time} - {config.shift_end_time}
                  {config.shift_name && <span className="text-wt-text-muted ml-1">({config.shift_name})</span>}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-wt-gold" />
                <span className="text-wt-text-muted">Grace:</span>
                <span className="text-wt-text-primary">{config.grace_period_minutes} min</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-wt-gold" />
                <span className="text-wt-text-muted">Late after:</span>
                <span className="text-wt-text-primary">{config.late_threshold_minutes} min</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Section */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-wt-gold" />
            My Attendance Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-wt-border"
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-wt-text-primary font-medium min-w-[120px] text-center">
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-wt-border"
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500"></div>
              <span className="text-wt-text-secondary">Present</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-yellow-500"></div>
              <span className="text-wt-text-secondary">Late</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-500"></div>
              <span className="text-wt-text-secondary">Half Day</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span className="text-wt-text-secondary">Absent</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span className="text-wt-text-secondary">Leave</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-500"></div>
              <span className="text-wt-text-secondary">Week Off</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-wt-text-muted py-2">
                {day}
              </div>
            ))}
            {(() => {
              const year = calendarMonth.getFullYear();
              const month = calendarMonth.getMonth();
              const firstDay = new Date(year, month, 1).getDay();
              const daysInMonth = new Date(year, month + 1, 0).getDate();
              const days = [];
              
              // Get employee's shift working days
              const employeeShift = employeeData?.hr_data?.assigned_shift_id 
                ? shifts.find(s => s.id === employeeData.hr_data.assigned_shift_id)
                : shifts.find(s => s.is_default);
              const jsToBackendDay = (jsDay) => jsDay === 0 ? 6 : jsDay - 1;
              const workingDays = employeeShift?.working_days || [0, 1, 2, 3, 4, 5];
              
              // Empty cells before first day
              for (let i = 0; i < firstDay; i++) {
                days.push(<div key={`empty-${i}`} className="h-12"></div>);
              }
              
              for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const record = attendanceRecords.find(r => r.date === dateStr);
                const jsDay = new Date(year, month, day).getDay();
                const backendDay = jsToBackendDay(jsDay);
                const isWeekOff = !workingDays.includes(backendDay);
                const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                const isFuture = new Date(year, month, day) > new Date();
                
                let bgColor = 'bg-wt-bg/50';
                let borderColor = 'border-transparent';
                
                if (record) {
                  if (record.status === 'present' || record.status === 'on_time') {
                    bgColor = 'bg-green-500/20';
                    borderColor = 'border-green-500';
                  } else if (record.status === 'late') {
                    bgColor = 'bg-yellow-500/20';
                    borderColor = 'border-yellow-500';
                  } else if (record.status === 'half_day') {
                    bgColor = 'bg-orange-500/20';
                    borderColor = 'border-orange-500';
                  } else if (record.status === 'absent') {
                    bgColor = 'bg-red-500/20';
                    borderColor = 'border-red-500';
                  } else if (record.status === 'on_leave') {
                    bgColor = 'bg-blue-500/20';
                    borderColor = 'border-blue-500';
                  } else if (record.status === 'week_off') {
                    bgColor = 'bg-gray-500/10';
                  }
                } else if (isWeekOff) {
                  bgColor = 'bg-gray-500/10';
                }
                
                days.push(
                  <div
                    key={day}
                    className={`min-h-16 rounded flex flex-col items-center justify-center text-xs border ${bgColor} ${borderColor} ${isToday ? 'ring-2 ring-wt-gold' : ''} ${isFuture ? 'opacity-50' : ''} p-1`}
                  >
                    <span className={`font-medium ${isToday ? 'text-wt-gold' : 'text-wt-text-primary'}`}>{day}</span>
                    
                    {/* Show status label for special days */}
                    {record?.status === 'week_off' && (
                      <span className="text-[9px] text-gray-400 mt-0.5">Week Off</span>
                    )}
                    {record?.status === 'on_leave' && (
                      <span className="text-[9px] text-blue-400 mt-0.5">Leave</span>
                    )}
                    {record?.status === 'absent' && (
                      <span className="text-[9px] text-red-400 mt-0.5">Absent</span>
                    )}
                    {record?.status === 'half_day' && (
                      <span className="text-[9px] text-orange-400 mt-0.5">Half Day</span>
                    )}
                    
                    {/* Show week off label if no record but is week off day */}
                    {!record && isWeekOff && (
                      <span className="text-[9px] text-gray-400 mt-0.5">Week Off</span>
                    )}
                    
                    {/* Show check-in/out times for attendance records */}
                    {record && !['absent', 'on_leave', 'week_off'].includes(record.status) && (
                      <div className="flex flex-col items-center mt-0.5">
                        {record.check_in_time && (
                          <span className="text-[9px] text-green-400">
                            In: {formatTimeForCalendar(record.check_in_time)}
                          </span>
                        )}
                        {record.check_out_time && (
                          <span className="text-[9px] text-blue-400">
                            Out: {formatTimeForCalendar(record.check_out_time)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              
              return days;
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Leave Section */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            <FileText className="h-5 w-5 text-wt-gold" />
            My Leave Status
          </CardTitle>
          <Button
            onClick={() => setLeaveDialogOpen(true)}
            className="bg-wt-gold text-wt-bg hover:bg-wt-gold/90"
            size="sm"
          >
            <Send className="h-4 w-4 mr-2" />
            Apply Leave
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Leave Balance Cards */}
          {leaveBalance && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-wt-bg rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Palmtree className="h-5 w-5 text-green-400" />
                  <span className="text-wt-text-secondary text-sm">Annual Leave</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-green-400">
                    {leaveBalance.paid_leave?.remaining || 0}
                  </span>
                  <span className="text-wt-text-muted text-sm">
                    / {leaveBalance.paid_leave?.total || 30} days
                  </span>
                </div>
                {leaveBalance.paid_leave?.used > 0 && (
                  <p className="text-xs text-wt-text-muted mt-1">
                    {leaveBalance.paid_leave.used} days used this year
                  </p>
                )}
              </div>
              
              <div className="p-4 bg-wt-bg rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <HeartPulse className="h-5 w-5 text-red-400" />
                  <span className="text-wt-text-secondary text-sm">Sick Leave</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-red-400">
                    {leaveBalance.sick_leave?.remaining || 0}
                  </span>
                  <span className="text-wt-text-muted text-sm">
                    / {leaveBalance.sick_leave?.total || 10} days
                  </span>
                </div>
                {leaveBalance.sick_leave?.used > 0 && (
                  <p className="text-xs text-wt-text-muted mt-1">
                    {leaveBalance.sick_leave.used} days used this year
                  </p>
                )}
              </div>
              
              <div className="p-4 bg-wt-bg rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Ban className="h-5 w-5 text-gray-400" />
                  <span className="text-wt-text-secondary text-sm">Unpaid Leave</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-gray-400">Unlimited</span>
                </div>
                <p className="text-xs text-wt-text-muted mt-1">Subject to approval</p>
              </div>
            </div>
          )}

          {/* Leave Requests List */}
          <div>
            <h4 className="text-wt-text-primary font-medium mb-3 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-wt-gold" />
              My Leave Requests
            </h4>
            {leaveRequests.length === 0 ? (
              <p className="text-wt-text-muted text-center py-8 bg-wt-bg rounded-lg">
                No leave requests yet. Apply for leave using the button above.
              </p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {leaveRequests.map((request) => (
                  <div 
                    key={request.id} 
                    className={`p-4 rounded-lg border cursor-pointer hover:border-wt-gold/50 transition-colors ${
                      request.status === 'approved' ? 'bg-green-500/5 border-green-500/20' :
                      request.status === 'rejected' ? 'bg-red-500/5 border-red-500/20' :
                      'bg-yellow-500/5 border-yellow-500/20'
                    }`}
                    onClick={() => setSelectedLeaveRequest(request)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-wt-text-primary font-medium capitalize">
                            {request.leave_type?.replace('_', ' ')} Leave
                          </p>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            request.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            request.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-wt-text-secondary">
                          {new Date(request.start_date).toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })} - {new Date(request.end_date).toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </p>
                        <p className="text-xs text-wt-text-muted mt-1">
                          {request.total_days || 1} day{(request.total_days || 1) > 1 ? 's' : ''}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-wt-text-muted hover:text-wt-gold"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLeaveRequest(request);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                    {request.status === 'rejected' && request.rejection_reason && (
                      <p className="text-xs text-red-400 mt-2 p-2 bg-red-500/10 rounded">
                        Reason: {request.rejection_reason}
                      </p>
                    )}
                    {request.status === 'approved' && request.approved_by_name && (
                      <p className="text-xs text-green-400 mt-2">
                        Approved by {request.approved_by_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Attendance Correction Section */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-wt-text-primary flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-400" />
              Attendance Corrections
            </CardTitle>
            <Button
              onClick={() => setCorrectionDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Request Correction
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-wt-text-muted">
            If you forgot to check-in/check-out or made an error, submit a correction request with proof.
          </p>

          {/* Correction Requests List */}
          <div>
            <h4 className="text-wt-text-primary font-medium mb-3 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-orange-400" />
              My Correction Requests
            </h4>
            {correctionRequests.length === 0 ? (
              <p className="text-wt-text-muted text-center py-8 bg-wt-bg rounded-lg">
                No correction requests yet.
              </p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {correctionRequests.map((request) => (
                  <div 
                    key={request.id} 
                    className={`p-4 rounded-lg border ${
                      request.status === 'approved' ? 'bg-green-500/5 border-green-500/20' :
                      request.status === 'rejected' ? 'bg-red-500/5 border-red-500/20' :
                      'bg-orange-500/5 border-orange-500/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-wt-text-primary font-medium">
                            {request.correction_type === 'check_in' ? 'Check-In' : 'Check-Out'} Correction
                          </p>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            request.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            request.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-wt-text-secondary">
                          Date: {new Date(request.date).toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </p>
                        <p className="text-sm text-wt-text-muted">
                          Requested Time: <span className="text-wt-gold">{request.requested_time}</span>
                        </p>
                        <p className="text-xs text-wt-text-muted mt-1 truncate" title={request.reason}>
                          Reason: {request.reason}
                        </p>
                      </div>
                      {request.screenshot_url && (
                        <a 
                          href={request.screenshot_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Camera className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                    {request.status === 'rejected' && request.review_notes && (
                      <p className="text-xs text-red-400 mt-2 p-2 bg-red-500/10 rounded">
                        Reason: {request.review_notes}
                      </p>
                    )}
                    {request.status === 'approved' && request.reviewed_by_name && (
                      <p className="text-xs text-green-400 mt-2">
                        Approved by {request.reviewed_by_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Correction Request Dialog */}
      <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-400" />
              Request Attendance Correction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Date *</Label>
              <DateInput
                value={correctionFormData.date}
                onChange={(e) => setCorrectionFormData({...correctionFormData, date: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Correction Type *</Label>
              <Select 
                value={correctionFormData.correction_type} 
                onValueChange={(value) => setCorrectionFormData({...correctionFormData, correction_type: value})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="check_in">Check-In Time</SelectItem>
                  <SelectItem value="check_out">Check-Out Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Correct Time *</Label>
              <TimeInput
                value={correctionFormData.requested_time}
                onChange={(e) => setCorrectionFormData({...correctionFormData, requested_time: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Reason *</Label>
              <Textarea
                value={correctionFormData.reason}
                onChange={(e) => setCorrectionFormData({...correctionFormData, reason: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary resize-none"
                rows={3}
                placeholder="Explain why you need this correction..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Screenshot/Proof URL (Optional)</Label>
              <Input
                type="url"
                value={correctionFormData.screenshot_url}
                onChange={(e) => setCorrectionFormData({...correctionFormData, screenshot_url: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="https://..."
              />
              <p className="text-xs text-wt-text-muted">
                Upload your proof to any image hosting service and paste the URL here
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setCorrectionDialogOpen(false)}
                className="border-wt-border text-wt-text-muted"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitCorrection}
                disabled={submittingCorrection}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {submittingCorrection ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Request Detail Dialog */}
      <Dialog open={!!selectedLeaveRequest} onOpenChange={() => setSelectedLeaveRequest(null)}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Leave Request Details</DialogTitle>
          </DialogHeader>
          {selectedLeaveRequest && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-wt-text-secondary">Type</span>
                <span className="text-wt-text-primary font-medium capitalize">
                  {selectedLeaveRequest.leave_type?.replace('_', ' ')} Leave
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-wt-text-secondary">Status</span>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  selectedLeaveRequest.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  selectedLeaveRequest.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {selectedLeaveRequest.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-wt-text-secondary">Duration</span>
                <span className="text-wt-text-primary">
                  {selectedLeaveRequest.total_days || 1} day{(selectedLeaveRequest.total_days || 1) > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-wt-text-secondary">From</span>
                <span className="text-wt-text-primary">
                  {new Date(selectedLeaveRequest.start_date).toLocaleDateString('en-US', { 
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-wt-text-secondary">To</span>
                <span className="text-wt-text-primary">
                  {new Date(selectedLeaveRequest.end_date).toLocaleDateString('en-US', { 
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                  })}
                </span>
              </div>
              <div className="pt-2 border-t border-wt-border">
                <span className="text-wt-text-secondary text-sm">Reason</span>
                <p className="text-wt-text-primary mt-1">{selectedLeaveRequest.reason || 'No reason provided'}</p>
              </div>
              {selectedLeaveRequest.status === 'approved' && (
                <div className="pt-2 border-t border-wt-border">
                  <span className="text-wt-text-secondary text-sm">Approved By</span>
                  <p className="text-green-400 mt-1">{selectedLeaveRequest.approved_by_name || 'Admin'}</p>
                  {selectedLeaveRequest.approved_at && (
                    <p className="text-xs text-wt-text-muted">
                      on {new Date(selectedLeaveRequest.approved_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
              {selectedLeaveRequest.status === 'rejected' && (
                <div className="pt-2 border-t border-wt-border">
                  <span className="text-wt-text-secondary text-sm">Rejection Reason</span>
                  <p className="text-red-400 mt-1">{selectedLeaveRequest.rejection_reason || 'Not specified'}</p>
                </div>
              )}
              <div className="text-xs text-wt-text-muted pt-2">
                Applied on {new Date(selectedLeaveRequest.created_at).toLocaleDateString()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedLeaveRequest(null)}
              className="border-wt-border"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Application Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Apply for Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Leave Type *</Label>
              <Select
                value={leaveFormData.leave_type}
                onValueChange={(value) => setLeaveFormData(prev => ({ ...prev, leave_type: value }))}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="casual">Casual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="annual">Annual Leave</SelectItem>
                  <SelectItem value="emergency">Emergency Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Start Date *</Label>
                <DateInput
                  value={leaveFormData.start_date}
                  onChange={(e) => setLeaveFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary [color-scheme:dark]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">End Date *</Label>
                <DateInput
                  value={leaveFormData.end_date}
                  onChange={(e) => setLeaveFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary [color-scheme:dark]"
                  min={leaveFormData.start_date}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Reason *</Label>
              <Textarea
                value={leaveFormData.reason}
                onChange={(e) => setLeaveFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Please provide a reason for your leave..."
                className="bg-wt-bg border-wt-border text-wt-text-primary min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLeaveDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitLeave}
              disabled={submittingLeave}
              className="bg-wt-gold text-wt-bg hover:bg-wt-gold/90"
            >
              {submittingLeave ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceMarkingPage;
